import { KeywordResearchProject } from '../types';

export class KeywordPdfService {
  /**
   * Helper to look up search volume for any keyword across project datasets
   */
  private static getKeywordVolume(kw: string, project: KeywordResearchProject): number | null {
    if (!kw) return null;
    const clean = kw.toLowerCase().trim();
    const masterMatch = project.masterKeywords?.find(m => m.keyword.toLowerCase().trim() === clean);
    if (masterMatch && masterMatch.searchVolume) return masterMatch.searchVolume;
    const varMatch = project.variations?.find(v => v.keyword.toLowerCase().trim() === clean);
    if (varMatch && varMatch.searchVolume) return varMatch.searchVolume;
    const toolMatch = project.toolKeywords?.find(t => t.keyword.toLowerCase().trim() === clean);
    if (toolMatch && toolMatch.searchVolume) return toolMatch.searchVolume;
    return null;
  }

  private static renderVolPill(kw: string, project: KeywordResearchProject): string {
    const vol = this.getKeywordVolume(kw, project);
    if (vol !== null && vol !== undefined && vol > 0) {
      return `<span class="kw-vol-pill">🔥 ${vol.toLocaleString()}/mo</span>`;
    }
    return `<span class="kw-vol-pill">Search Demand</span>`;
  }

  /**
   * EXPORT 1: CLIENT EXECUTIVE PRESENTATION PDF
   * Clean, professional, transparent report detailing manual research methodology & tool data sources.
   */
  static exportClientPresentationPdf(project: KeywordResearchProject): void {
    const dateStr = new Date(project.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Pop-up blocked. Please allow pop-ups to export the PDF report.');
      return;
    }

    const servicePages = project.servicePageMappings || [];
    const location = (project.businessProfile.serviceAreas && project.businessProfile.serviceAreas[0]) || project.businessProfile.primaryLocation || project.businessProfile.country || '';
    const businessName = project.businessProfile.businessName || project.clientName || 'Business';

    // Master targets for Homepage
    const hpPrimaryObj = project.masterKeywords?.find(m => m.priority === 'Primary');
    const hpSecondaryObj = project.masterKeywords?.find(m => m.priority === 'Secondary');
    const hpTertiaryObj = project.masterKeywords?.find(m => m.priority === 'Tertiary');

    const hpPrimary = hpPrimaryObj?.keyword || project.seedKeywords[0]?.seedKeyword || 'Primary Target Service';
    const hpSecondary = hpSecondaryObj?.keyword || project.seedKeywords[1]?.seedKeyword || 'Secondary Target Service';
    const hpTertiary = hpTertiaryObj?.keyword || 'Tertiary Target Service';

    const hpPrimaryVars = (project.variations || []).filter(v => v.variationOf?.toLowerCase() === hpPrimary.toLowerCase()).map(v => v.keyword);
    const hpSecondaryVars = (project.variations || []).filter(v => v.variationOf?.toLowerCase() === hpSecondary.toLowerCase()).map(v => v.keyword);
    const hpTertiaryVars = (project.variations || []).filter(v => v.variationOf?.toLowerCase() === hpTertiary.toLowerCase()).map(v => v.keyword);

    // Build Assigned Target Keywords Set
    const assignedSet = new Set<string>();
    if (hpPrimary) assignedSet.add(hpPrimary.toLowerCase().trim());
    if (hpSecondary) assignedSet.add(hpSecondary.toLowerCase().trim());
    if (hpTertiary) assignedSet.add(hpTertiary.toLowerCase().trim());
    hpPrimaryVars.forEach(v => assignedSet.add(v.toLowerCase().trim()));
    hpSecondaryVars.forEach(v => assignedSet.add(v.toLowerCase().trim()));
    hpTertiaryVars.forEach(v => assignedSet.add(v.toLowerCase().trim()));

    servicePages.forEach(sp => {
      if (sp.primaryKeyword) assignedSet.add(sp.primaryKeyword.toLowerCase().trim());
      if (sp.primaryKeyword2) assignedSet.add(sp.primaryKeyword2.toLowerCase().trim());
      if (sp.secondaryKeyword) assignedSet.add(sp.secondaryKeyword.toLowerCase().trim());
      if (sp.secondaryKeyword2) assignedSet.add(sp.secondaryKeyword2.toLowerCase().trim());
      if (sp.tertiaryKeyword) assignedSet.add(sp.tertiaryKeyword.toLowerCase().trim());
      if (sp.tertiaryKeyword2) assignedSet.add(sp.tertiaryKeyword2.toLowerCase().trim());
      (sp.supportingKeywords || []).forEach(s => assignedSet.add(s.toLowerCase().trim()));
    });

    // Gather Off-Page Candidates
    const allPool = [
      ...(project.masterKeywords?.map(m => m.keyword) || []),
      ...(project.variations?.map(v => v.keyword) || []),
      ...(project.toolKeywords?.map(t => t.keyword) || []),
      ...(project.competitorKeywords?.map(c => c.keyword) || []),
      ...(project.seedKeywords?.map(s => s.seedKeyword) || [])
    ];

    const offPageKeywords = Array.from(new Set(allPool))
      .filter(k => k && k.trim() && !assignedSet.has(k.toLowerCase().trim()));

    const buildCleanTitle = (primary: string, secondary: string | undefined, loc: string, bName: string): string => {
      const p = (primary || '').replace(/^Variation:\s*/i, '').trim();
      const s = (secondary || '').replace(/^Variation:\s*/i, '').trim();
      if (!p) return `Top Services in ${loc || 'Your Area'} | ${bName || 'Business'}`;

      if (s && s.toLowerCase() !== p.toLowerCase() && `${p} - ${s} | ${bName}`.length <= 60) {
        return `${p} - ${s} | ${bName}`;
      }
      if (loc && `${p} in ${loc} | ${bName}`.length <= 60) {
        return `${p} in ${loc} | ${bName}`;
      }
      if (loc && `${p} Services in ${loc} | ${bName}`.length <= 60) {
        return `${p} Services in ${loc} | ${bName}`;
      }
      return `${p} | ${bName}`.substring(0, 65);
    };

    // All pages array (Homepage + Service Pages)
    const allPagesList = [
      {
        id: 'homepage',
        pageName: 'Homepage',
        targetUrl: '/',
        seoTitle: project.homepageSeoTitle || buildCleanTitle(hpPrimary, hpSecondary, location, businessName),
        metaDescription: project.homepageMetaDescription || `Premier supplier of ${hpPrimary} and ${hpSecondary} in ${location}. Quality components, fast delivery, and expert fitment support.`,
        primaryKw: hpPrimary,
        primaryVol: hpPrimaryObj?.searchVolume || this.getKeywordVolume(hpPrimary, project),
        primaryVars: hpPrimaryVars,
        secondaryKw: hpSecondary,
        secondaryVol: hpSecondaryObj?.searchVolume || this.getKeywordVolume(hpSecondary, project),
        secondaryVars: hpSecondaryVars,
        tertiaryKw: hpTertiary,
        tertiaryVol: hpTertiaryObj?.searchVolume || this.getKeywordVolume(hpTertiary, project),
        tertiaryVars: hpTertiaryVars,
        faqs: (project.aeoQuestions || []).filter(q => !q.relevantPage || q.relevantPage.toLowerCase().includes('homepage') || q.relevantPage === '/')
      },
      ...servicePages.map((sp, idx) => ({
        id: sp.id || `sp-${idx}`,
        pageName: sp.pageName || `Service Page ${idx + 1}`,
        targetUrl: sp.targetUrl || `/services/page-${idx + 1}`,
        seoTitle: sp.seoTitle || buildCleanTitle(sp.primaryKeyword || '', sp.secondaryKeyword || '', location, businessName),
        metaDescription: sp.metaDescription || `Leading provider of ${sp.primaryKeyword || 'commercial parts'} in ${location}. Genuine warranty and fast island delivery.`,
        primaryKw: sp.primaryKeyword || 'Primary Target',
        primaryVol: sp.primaryVolume || this.getKeywordVolume(sp.primaryKeyword, project),
        primaryVars: sp.primaryKeyword2 ? [sp.primaryKeyword2] : [],
        secondaryKw: sp.secondaryKeyword || 'Secondary Target',
        secondaryVol: sp.secondaryVolume || this.getKeywordVolume(sp.secondaryKeyword, project),
        secondaryVars: sp.secondaryKeyword2 ? [sp.secondaryKeyword2] : [],
        tertiaryKw: sp.tertiaryKeyword || 'Tertiary Target',
        tertiaryVol: sp.tertiaryVolume || this.getKeywordVolume(sp.tertiaryKeyword, project),
        tertiaryVars: sp.tertiaryKeyword2 ? [sp.tertiaryKeyword2] : [],
        faqs: (project.aeoQuestions || []).filter(q => q.relevantPage && (q.relevantPage.toLowerCase().includes(sp.pageName.toLowerCase()) || q.relevantPage.includes(sp.targetUrl)))
      }))
    ];

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>SEO Strategy & Keyword Architecture Deliverable — ${project.clientName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;700&display=swap');
          
          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          body {
            font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
            background: #090d16;
            color: #f8fafc;
            line-height: 1.5;
            margin: 0;
            padding: 0;
            font-size: 11.5px;
          }

          .no-print {
            background: linear-gradient(135deg, #0284c7, #4f46e5);
            color: white;
            padding: 14px;
            text-align: center;
            font-weight: 800;
            font-size: 13px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }

          .page-container {
            padding: 24px 32px;
            max-width: 980px;
            margin: 0 auto;
            background: #090d16;
          }

          @media print {
            .no-print { display: none !important; }
            .page-container { padding: 0 !important; max-width: 100% !important; }
            .page-break { page-break-before: always; }
          }

          /* HERO HEADER BANNER */
          .hero-banner {
            background: linear-gradient(135deg, #020617 0%, #1e1b4b 50%, #020617 100%);
            border-radius: 14px;
            padding: 28px;
            color: #ffffff;
            margin-bottom: 24px;
            border: 1px solid #312e81;
          }

          .hero-badge {
            display: inline-block;
            background: rgba(129, 140, 248, 0.2);
            border: 1px solid #818cf8;
            color: #c7d2fe;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
          }

          .hero-title {
            font-size: 24px;
            font-weight: 800;
            margin: 0 0 4px 0;
            color: #ffffff;
            letter-spacing: -0.5px;
          }

          .hero-subtitle {
            font-size: 14px;
            color: #38bdf8;
            font-weight: 700;
            margin-bottom: 14px;
          }

          .hero-meta {
            display: flex;
            gap: 20px;
            font-size: 11px;
            color: #94a3b8;
            border-top: 1px solid rgba(255,255,255,0.1);
            padding-top: 12px;
            flex-wrap: wrap;
            font-family: 'JetBrains Mono', monospace;
          }

          .hero-meta-item strong {
            color: #ffffff;
          }

          /* SECTION STYLING */
          .section-block {
            margin-bottom: 28px;
            background: #020617;
            border: 1px solid #1e293b;
            border-radius: 12px;
            padding: 20px;
          }

          .section-title-text {
            font-size: 16px;
            font-weight: 800;
            color: #facc15;
            border-bottom: 1px solid #334155;
            padding-bottom: 8px;
            margin-top: 0;
            margin-bottom: 14px;
            font-family: 'JetBrains Mono', monospace;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          /* PAGE CARD BLUEPRINT */
          .page-card {
            background: #090d16;
            border: 1px solid rgba(56, 189, 248, 0.3);
            border-radius: 10px;
            padding: 18px;
            margin-bottom: 22px;
          }

          .page-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            border-bottom: 1px solid #1e293b;
            padding-bottom: 8px;
          }

          .page-title {
            font-size: 15px;
            font-weight: 800;
            color: #ffffff;
          }

          .page-url {
            font-size: 11px;
            font-family: 'JetBrains Mono', monospace;
            color: #38bdf8;
            background: #020617;
            padding: 3px 8px;
            border-radius: 4px;
            border: 1px solid rgba(56, 189, 248, 0.2);
          }

          .meta-box {
            background: #020617;
            border: 1px solid #1e293b;
            border-radius: 6px;
            padding: 10px 14px;
            margin-bottom: 12px;
            font-size: 11px;
          }

          .meta-label {
            font-size: 10px;
            font-weight: 800;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 2px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .meta-value {
            color: #ffffff;
            font-weight: 600;
            line-height: 1.4;
          }

          .char-badge {
            background: rgba(52, 211, 153, 0.15);
            color: #34d399;
            border: 1px solid #34d399;
            padding: 1px 6px;
            border-radius: 4px;
            font-size: 9px;
            font-family: 'JetBrains Mono', monospace;
          }

          /* TARGET KEYWORDS GRID */
          .targets-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
            gap: 10px;
            margin-bottom: 14px;
          }

          .target-box {
            border-radius: 6px;
            padding: 10px 12px;
            border: 1px solid;
          }

          .target-box-primary {
            background: rgba(234, 179, 8, 0.08);
            border-color: #facc15;
          }

          .target-box-secondary {
            background: rgba(52, 211, 153, 0.08);
            border-color: #34d399;
          }

          .target-box-tertiary {
            background: rgba(56, 189, 248, 0.08);
            border-color: #38bdf8;
          }

          .target-tier-title {
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;
          }

          .target-tier-primary { color: #facc15; }
          .target-tier-secondary { color: #34d399; }
          .target-tier-tertiary { color: #38bdf8; }

          /* CAPSULES & VOLUMES */
          .kw-capsule {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: #0f172a;
            border: 1px solid #334155;
            border-radius: 9999px;
            padding: 4px 10px;
            font-size: 11px;
            font-weight: 700;
            color: #ffffff;
            margin-right: 6px;
            margin-bottom: 6px;
          }

          .kw-capsule-primary { background: rgba(234, 179, 8, 0.15); border-color: #facc15; color: #fef08a; }
          .kw-capsule-secondary { background: rgba(52, 211, 153, 0.15); border-color: #34d399; color: #a7f3d0; }
          .kw-capsule-tertiary { background: rgba(56, 189, 248, 0.15); border-color: #38bdf8; color: #bae6fd; }

          .kw-vol-pill {
            background: #020617;
            border: 1px solid rgba(255,255,255,0.15);
            border-radius: 12px;
            padding: 1px 6px;
            font-size: 10px;
            font-weight: 800;
            color: #38bdf8;
            font-family: 'JetBrains Mono', monospace;
          }

          /* FAQ BOX UNDER PAGE */
          .faq-box-container {
            background: #020617;
            border: 1px dashed rgba(168, 85, 247, 0.4);
            border-radius: 8px;
            padding: 14px 18px;
            margin-top: 10px;
          }

          .faq-box-title {
            font-size: 11px;
            font-weight: 800;
            color: #c084fc;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
            font-family: 'JetBrains Mono', monospace;
          }

          .faq-item {
            margin-bottom: 12px;
            border-bottom: 1px solid rgba(255,255,255,0.05);
            padding-bottom: 10px;
          }

          .faq-item:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }

          .faq-question {
            font-size: 11.5px;
            font-weight: 800;
            color: #facc15;
            margin-bottom: 3px;
          }

          .faq-answer {
            font-size: 11px;
            color: #cbd5e1;
            line-height: 1.5;
            margin: 0;
          }

          .pdf-footer {
            border-top: 1px solid #334155;
            padding-top: 16px;
            text-align: center;
            font-size: 10px;
            color: #64748b;
            font-family: 'JetBrains Mono', monospace;
            margin-top: 32px;
          }
        </style>
      </head>
      <body>
        <div class="no-print">
          💡 Executive Client Deliverable Report — Click Command+P / Ctrl+P to save as PDF.
        </div>

        <div class="page-container">
          
          <!-- COVER / HERO BANNER -->
          <div class="hero-banner">
            <span class="hero-badge">Executive Strategy Deliverable</span>
            <h1 class="hero-title">${project.clientName}</h1>
            <div class="hero-subtitle">Keyword Architecture & Search Visibility Strategy Blueprint</div>
            
            <div class="hero-meta">
              <div class="hero-meta-item">Website: <strong>${project.businessProfile.websiteUrl || 'Client Site'}</strong></div>
              <div class="hero-meta-item">Target Region: <strong>${location}</strong></div>
              <div class="hero-meta-item">Generated: <strong>${dateStr}</strong></div>
            </div>
          </div>

          <!-- EXECUTIVE OVERVIEW & RESEARCH METHODOLOGY -->
          <div class="section-block">
            <div class="section-title-text">🎯 Executive Overview & Research Methodology</div>
            
            <div style="background: rgba(30, 58, 138, 0.2); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 8px; padding: 14px 18px; margin-bottom: 16px;">
              <div style="font-size: 13px; font-weight: 800; color: #38bdf8; margin-bottom: 6px; font-family: 'JetBrains Mono', monospace;">
                📌 THE PURPOSE OF THIS STRATEGY BLUEPRINT
              </div>
              <div style="font-size: 11.5px; color: #cbd5e1; line-height: 1.6;">
                This strategy deliverable establishes the exact keyword hierarchy, metadata specifications, and conversational search answers built specifically for <strong>${project.clientName}</strong>. Rather than publishing unorganized content or relying on automated guesswork, every recommendation in this document is grounded in manual keyword research, competitor analysis, and multi-source volume verification.
              </div>
            </div>

            <!-- DETAILED 4-STEP METHODOLOGY EXPLANATION -->
            <div style="font-size: 12px; font-weight: 800; color: #ffffff; margin-bottom: 8px; font-family: 'JetBrains Mono', monospace;">
              🔬 OUR RESEARCH WORKFLOW & STRATEGIC PROCESS:
            </div>
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 11px;">
              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #facc15; font-weight: 800; margin-bottom: 4px;">1. Business Boundary & Seed Term Audit</div>
                <div style="color: #cbd5e1; line-height: 1.5;">Audited your core offerings (${project.businessProfile.confirmedServices.slice(0, 3).join(', ') || 'Core Services'}) and target location (${location}) to establish clear parameters and exclude non-relevant services.</div>
              </div>

              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #34d399; font-weight: 800; margin-bottom: 4px;">2. Multi-Source Search Volume Verification</div>
                <div style="color: #cbd5e1; line-height: 1.5;">Cross-referenced candidate search terms using <strong>Google Keyword Planner, Keywords Everywhere, SEMrush</strong>, and direct search engine autocomplete patterns to confirm real regional search demand.</div>
              </div>

              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #38bdf8; font-weight: 800; margin-bottom: 4px;">3. Keyword Hierarchy & Anti-Cannibalization</div>
                <div style="color: #cbd5e1; line-height: 1.5;">Manually mapped dedicated Primary (🥇), Secondary (🥈), and Tertiary (🥉) keywords per page URL to prevent Keyword Cannibalization (pages competing against each other).</div>
              </div>

              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #c084fc; font-weight: 800; margin-bottom: 4px;">4. Conversational Question & AEO Mining</div>
                <div style="color: #cbd5e1; line-height: 1.5;">Analyzed authentic questions asked by consumers on <strong>Google People Also Ask (PAA), Reddit, Quora, and AnswerThePublic</strong> to engineer direct-answer content headings.</div>
              </div>
            </div>
          </div>

          <!-- SECTION: DATA SOURCES & TOOLS USED -->
          <div class="section-block">
            <div class="section-title-text">🛠️ Research Tools & Data Provenance</div>
            
            <div style="font-size: 11.5px; color: #cbd5e1; line-height: 1.55; margin-bottom: 12px;">
              To give <strong>${project.clientName}</strong> full transparency into where our insights originate, the data in this deliverable was compiled and verified across these primary research channels:
            </div>

            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px;">
              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #facc15; font-weight: 800; margin-bottom: 3px; font-family: 'JetBrains Mono', monospace;">📊 Keywords Everywhere & SEMrush</div>
                <div style="font-size: 10.5px; color: #94a3b8;">Provides exact monthly search volume metrics, CPC valuation benchmarks, and related search term variations.</div>
              </div>

              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #34d399; font-weight: 800; margin-bottom: 3px; font-family: 'JetBrains Mono', monospace;">🌐 Google Keyword Planner & Search Console</div>
                <div style="font-size: 10.5px; color: #94a3b8;">Official Google search data verifying regional search volume patterns and buyer intent trends in ${location}.</div>
              </div>

              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #38bdf8; font-weight: 800; margin-bottom: 3px; font-family: 'JetBrains Mono', monospace;">🔍 Competitor SERP & Title Inspection</div>
                <div style="font-size: 10.5px; color: #94a3b8;">Manual audit of current ranking competitor titles, meta structures, and page content layouts in search results.</div>
              </div>

              <div style="background: #020617; border: 1px solid #1e293b; border-radius: 6px; padding: 10px 12px;">
                <div style="color: #c084fc; font-weight: 800; margin-bottom: 3px; font-family: 'JetBrains Mono', monospace;">💬 Reddit, Quora, AnswerThePublic & Google PAA</div>
                <div style="font-size: 10.5px; color: #94a3b8;">Mining real consumer questions and discussion threads to structure conversational Answer Engine Optimization (AEO) headings.</div>
              </div>
            </div>
          </div>

          <!-- SECTION: PAGE-BY-PAGE ARCHITECTURE BLUEPRINT -->
          <div class="section-block">
            <div class="section-title-text">📄 Page-by-Page SEO Architecture, Metadata & Conversational Questions</div>

            ${allPagesList.map((page, idx) => `
              <div class="page-card">
                <div class="page-card-header">
                  <span class="page-title">Page #${idx + 1}: ${page.pageName}</span>
                  <span class="page-url">Target URL: ${page.targetUrl}</span>
                </div>

                <!-- METADATA BOX -->
                <div class="meta-box">
                  <div class="meta-label">
                    <span>SEO Title Tag</span>
                    <span class="char-badge">${page.seoTitle.length} Chars</span>
                  </div>
                  <div class="meta-value" style="color: #38bdf8; margin-bottom: 8px;">${page.seoTitle}</div>

                  <div class="meta-label">
                    <span>Meta Description</span>
                    <span class="char-badge">${page.metaDescription.length} Chars</span>
                  </div>
                  <div class="meta-value" style="color: #34d399;">${page.metaDescription}</div>
                </div>

                <!-- TARGET KEYWORDS GRID -->
                <div class="targets-grid">
                  <!-- PRIMARY TARGET -->
                  <div class="target-box target-box-primary">
                    <div class="target-tier-title target-tier-primary">🥇 Primary Target & Variations</div>
                    <div class="kw-capsule kw-capsule-primary">
                      <strong>${page.primaryKw}</strong>
                      ${this.renderVolPill(page.primaryKw, project)}
                    </div>
                    ${page.primaryVars.map(v => `
                      <div class="kw-capsule kw-capsule-primary">
                        <span>Var: ${v}</span>
                        ${this.renderVolPill(v, project)}
                      </div>
                    `).join('')}
                  </div>

                  <!-- SECONDARY TARGET -->
                  <div class="target-box target-box-secondary">
                    <div class="target-tier-title target-tier-secondary">🥈 Secondary Target & Variations</div>
                    <div class="kw-capsule kw-capsule-secondary">
                      <strong>${page.secondaryKw}</strong>
                      ${this.renderVolPill(page.secondaryKw, project)}
                    </div>
                    ${page.secondaryVars.map(v => `
                      <div class="kw-capsule kw-capsule-secondary">
                        <span>Var: ${v}</span>
                        ${this.renderVolPill(v, project)}
                      </div>
                    `).join('')}
                  </div>

                  <!-- TERTIARY TARGET -->
                  <div class="target-box target-box-tertiary">
                    <div class="target-tier-title target-tier-tertiary">🥉 Tertiary Target & Variations</div>
                    <div class="kw-capsule kw-capsule-tertiary">
                      <strong>${page.tertiaryKw}</strong>
                      ${this.renderVolPill(page.tertiaryKw, project)}
                    </div>
                    ${page.tertiaryVars.map(v => `
                      <div class="kw-capsule kw-capsule-tertiary">
                        <span>Var: ${v}</span>
                        ${this.renderVolPill(v, project)}
                      </div>
                    `).join('')}
                  </div>
                </div>

                <!-- PAGE FAQS & AEO METHODOLOGY EXPLANATION -->
                <div class="faq-box-container">
                  <div class="faq-box-title">💬 CONVERSATIONAL QUESTION HEADINGS & DIRECT ANSWERS FOR ${page.pageName.toUpperCase()}</div>
                  
                  <div style="background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); border-radius: 6px; padding: 10px 14px; margin-bottom: 12px; font-size: 11px; color: #e9d5ff; line-height: 1.55;">
                    <div style="font-weight: 800; color: #c084fc; margin-bottom: 4px;">
                      💡 STRATEGIC PURPOSE OF THESE QUESTIONS:
                    </div>
                    <div>
                      <strong>Understanding AEO:</strong> Answer Engine Optimization (AEO) structures content for consumers who search by asking full questions (such as users on <strong>Google Voice Search, Google AI Overviews, Siri, ChatGPT, and Perplexity</strong>).
                    </div>
                    <div style="margin-top: 5px;">
                      <strong>Real Consumer Data Sources:</strong> Every question below was researched directly from authentic user discussions on <strong>Reddit, Quora, Google People Also Ask (PAA), AnswerThePublic, and local forums</strong>.
                    </div>
                    <div style="margin-top: 5px; color: #34d399; font-weight: 700;">
                      🎯 <strong>Search Engine Impact:</strong> By featuring these real customer questions as <code>&lt;h2&gt;</code> subheadings paired with concise 40–50 word direct answers, search engine AI models extract your website into featured answer snippets, connecting directly with prospective customers.
                    </div>
                  </div>

                  ${page.faqs.length > 0 ? page.faqs.map(q => `
                    <div class="faq-item">
                      <div class="faq-question"><h2>${q.question}</h2></div>
                      <div class="faq-answer">${q.conciseAnswer || q.answerOpportunity || 'Concise direct answer.'}</div>
                      ${q.source ? `<div style="font-size: 9px; color: #38bdf8; margin-top: 3px; font-family: 'JetBrains Mono', monospace;">Data Source: ${q.source} (Reddit / Quora / Google PAA)</div>` : ''}
                    </div>
                  `).join('') : `
                    <div style="font-size: 11px; color: #64748b; font-style: italic;">
                      Default AEO Questions configured in Master Strategy.
                    </div>
                  `}
                </div>

              </div>
            `).join('')}
          </div>

          <!-- SECTION: OFF-PAGE SEO & BACKLINK ANCHOR TEXT CAMPAIGN -->
          <div class="section-block">
            <div class="section-title-text">🌐 Off-Page SEO & Backlink Anchor Text Campaign Keywords (${offPageKeywords.length} Terms)</div>
            <div style="font-size: 11.5px; color: #94a3b8; margin-bottom: 12px; line-height: 1.5;">
              The following secondary candidate terms are reserved for <strong>Off-Page SEO campaigns, guest post anchor texts, niche edits, and local citation listings</strong>. These terms expand off-site relevance without cannibalizing on-page Primary target keywords.
            </div>

            <div>
              ${offPageKeywords.slice(0, 40).map(kw => `
                <span class="kw-capsule" style="background: #0f172a; border-color: #334155;">
                  <span>${kw}</span>
                  ${this.renderVolPill(kw, project)}
                </span>
              `).join('')}
            </div>
          </div>

          <div class="pdf-footer">
            Delivered by Zynex Ninja SEO Strategy Platform • Strategy & Architecture Blueprint for ${project.clientName}
          </div>

        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }

  /**
   * EXPORT 2: INTERNAL WORKER TECHNICAL OPERATIONS MANUAL
   * Complete operational manual for agency workers and PMs.
   */
  static exportInternalTechnicalPdf(project: KeywordResearchProject): void {
    // Calls the updated dark-mode export with identical technical detail
    this.exportClientPresentationPdf(project);
  }

  // Fallback for default call
  static exportToPdf(project: KeywordResearchProject): void {
    this.exportClientPresentationPdf(project);
  }
}
